import React, { useEffect, useState } from "react";
import { useApplicantsWithATS } from "../../../../../data/applicants";
import { getAppliedConfirmationForJob } from "../../../../../data/listings";

const PAGE_SIZE = 10;

const formatVerifiedApplied = (appliedConfirmation: string | null) => {
    if (appliedConfirmation === "yes") return "Yes";
    if (appliedConfirmation === "no") return "No";
    if (appliedConfirmation) return "Unknown";
    return "";
};

const ApplyNowCounterSeekerList = ({ jobId, setApplyNowSkrData }: any) => {
    const [groupedApplicants, setGroupedApplicants] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const { data } = useApplicantsWithATS(jobId);

    useEffect(() => {
        if (!data || data.length === 0) {
            setGroupedApplicants([]);
            return;
        }

        const processApplicants = async () => {
            setLoading(true);

            const grouped: Record<string, any> = {};

            // 🔹 Step 1: Group by userId and count clicks
            data.forEach((item: any) => {
                const userId = item.userId;

                const timestamp =
                    item.createdAt?.seconds * 1e9 +
                    item.createdAt?.nanoseconds;

                if (!grouped[userId]) {
                    grouped[userId] = {
                        ...item,
                        __timestamp: timestamp,
                        count: 1,
                    };
                } else {
                    grouped[userId].count += 1;

                    if (timestamp > grouped[userId].__timestamp) {
                        grouped[userId] = {
                            ...item,
                            count: grouped[userId].count,
                            __timestamp: timestamp,
                        };
                    }
                }
            });

            const groupedArray = Object.values(grouped);

            // 🔹 Step 2: Check applied confirmation for this job only
            const updatedApplicants = await Promise.all(
                groupedArray.map(async (applicant: any) => {
                    try {
                        const confirmationForJob =
                            await getAppliedConfirmationForJob(
                                applicant.userId,
                                jobId
                            );

                        return {
                            ...applicant,
                            isApplied: !!confirmationForJob,
                            appliedConfirmation:
                                confirmationForJob?.appliedConfirmation || null,
                        };
                    } catch (error) {
                        console.error(
                            "Error fetching applied jobs for user:",
                            applicant.userId,
                            error
                        );

                        return {
                            ...applicant,
                            isApplied: false,
                            appliedConfirmation: null,
                        };
                    }
                })
            );

            // 🔹 Step 3: Clean unwanted fields
            const cleaned = updatedApplicants.map(
                ({
                    createdAt,
                    __timestamp,
                    type,
                    platform,
                    solutionType,
                    jobId,
                    id,
                    ...rest
                }: any) => rest
            );

            // 🔹 Step 4: Send data for export
            setApplyNowSkrData(
                cleaned.map((data: any) => ({
                    Name: data.name,
                    Email: data.email,
                    "Phone Number": data.phone,
                    "Number of Clicks": data.count,
                    "Verified Applied": formatVerifiedApplied(
                        data.appliedConfirmation
                    ),
                }))
            );

            setGroupedApplicants(cleaned);
            setLoading(false);
        };

        processApplicants();
    }, [data, jobId, setApplyNowSkrData]);

    // 🔹 Pagination Logic
    const totalPages = Math.ceil(groupedApplicants.length / PAGE_SIZE);

    const paginatedData = groupedApplicants.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getPageNumbers = (): (number | string)[] => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const pages: (number | string)[] = [1];

        if (currentPage > 3) {
            pages.push("...");
        }

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (currentPage < totalPages - 2) {
            pages.push("...");
        }

        pages.push(totalPages);

        return pages;
    };

    return (
        <div className="p-4">
            <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200 bg-white">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-instant-teams-blue-Main">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-instant-teams-blue-Main">
                                Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-instant-teams-blue-Main">
                                Phone Number
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-instant-teams-blue-Main">
                                Number Of Clicks
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-instant-teams-blue-Main">
                                Verified Applied
                            </th>
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-100">
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-6 py-4 text-center text-sm text-gray-500"
                                >
                                    Loading...
                                </td>
                            </tr>
                        ) : paginatedData.length > 0 ? (
                            paginatedData.map((item: any, index: number) => (
                                <tr key={item.userId || index}>
                                    <td className="px-6 py-4 text-sm text-instant-teams-blue-Main">
                                        {item.name}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-instant-teams-blue-Main">
                                        {item.email}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-instant-teams-blue-Main">
                                        {item.phone}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-center text-instant-teams-blue-Main">
                                        {item.count}
                                    </td>
                                    <td className="px-6 py-4 text-sm ">
                                        {item.isApplied ? (
                                            <div className="flex w-full justify-center ">
                                                <span>
                                                    {formatVerifiedApplied(
                                                        item.appliedConfirmation
                                                    )}
                                                </span>
                                            </div>
                                        ) : ""}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-6 py-4 text-center text-sm text-gray-500"
                                >
                                    No Data
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-end mt-6 gap-2">
                    <button
                        onClick={() =>
                            handlePageChange(currentPage - 1)
                        }
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded ${currentPage !== 1
                            ? "bg-instant-teams-blue-Main text-white"
                            : "bg-gray-200"
                            } disabled:opacity-50`}
                    >
                        Prev
                    </button>

                    {getPageNumbers().map((page, idx) =>
                        typeof page === "string" ? (
                            <span
                                key={`ellipsis-${idx}`}
                                className="px-3 py-1 text-gray-500 select-none"
                            >
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-3 py-1 rounded ${
                                    currentPage === page
                                        ? "bg-instant-teams-blue-Main text-white"
                                        : "bg-gray-200"
                                }`}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        onClick={() =>
                            handlePageChange(currentPage + 1)
                        }
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded ${currentPage !== totalPages
                            ? "bg-instant-teams-blue-Main text-white"
                            : "bg-gray-200"
                            } disabled:opacity-50`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default ApplyNowCounterSeekerList;